import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { User, MapPin, GraduationCap, Award, ChevronDown, ChevronUp, Edit, AlertCircle, Phone, Mail, School } from "lucide-react";
import { useState } from "react";

// StudentInfo 类型从 api/chat.ts 导入
import type { StudentInfo } from '@/api/chat';

interface MobileStudentInfoCardProps {
  studentInfo: StudentInfo;
  completionRate: number;
  isExpanded?: boolean;
  onToggle?: () => void;
  onEdit?: () => void;
  educationLevel?: 'senior' | 'junior';
}

export function MobileStudentInfoCard({
  studentInfo,
  completionRate,
  isExpanded = false,
  onToggle,
  onEdit,
  educationLevel = 'senior',
}: MobileStudentInfoCardProps) {
  const [isExpandedState, setIsExpandedState] = useState(isExpanded);
  const isJunior = educationLevel === 'junior';

  const handleToggle = () => {
    setIsExpandedState(!isExpandedState);
    onToggle?.();
  };

  // 扩展 StudentInfo 类型以包含联系信息
  const extendedStudentInfo = studentInfo as StudentInfo & {
    phone?: string;
    email?: string;
    parentPhone?: string;
  };

  const getCompletionColor = () => {
    if (completionRate >= 80) return "text-green-600";
    if (completionRate >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getStreamText = () => {
    if (isJunior) return "无选科";
    if (studentInfo.stream === "physics") return "物理类";
    if (studentInfo.stream === "history") return "历史类";
    return "未选择";
  };

  const getUniversityLevelBadge = () => {
    if (isJunior) {
      switch (studentInfo.targetUniversityLevel) {
        case "985":
          return <Badge variant="secondary" className="bg-purple-100 text-purple-800 text-xs px-1.5 py-0.5">重点高中</Badge>;
        case "211":
          return <Badge variant="secondary" className="bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5">一级达标</Badge>;
        case "双一流":
          return <Badge variant="secondary" className="bg-indigo-100 text-indigo-800 text-xs px-1.5 py-0.5">二级达标</Badge>;
        case "普通本科":
          return <Badge variant="secondary" className="bg-gray-100 text-gray-800 text-xs px-1.5 py-0.5">普通高中</Badge>;
        case "专科":
          return <Badge variant="secondary" className="bg-orange-100 text-orange-800 text-xs px-1.5 py-0.5">职高/中专</Badge>;
        default:
          return null;
      }
    }

    switch (studentInfo.targetUniversityLevel) {
      case "985":
        return <Badge variant="secondary" className="bg-purple-100 text-purple-800 text-xs px-1.5 py-0.5">985</Badge>;
      case "211":
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5">211</Badge>;
      case "双一流":
        return <Badge variant="secondary" className="bg-indigo-100 text-indigo-800 text-xs px-1.5 py-0.5">双一流</Badge>;
      case "普通本科":
        return <Badge variant="secondary" className="bg-gray-100 text-gray-800 text-xs px-1.5 py-0.5">普本</Badge>;
      case "专科":
        return <Badge variant="secondary" className="bg-orange-100 text-orange-800 text-xs px-1.5 py-0.5">专科</Badge>;
      default:
        return null;
    }
  };

  // 环形进度条 - 移动端更小
  const CircularProgress = ({ value }: { value: number }) => {
    const radius = 30;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (value / 100) * circumference;

    return (
      <div className="relative inline-flex items-center justify-center">
        <svg
          className="transform -rotate-90"
          width={radius * 2 + 10}
          height={radius * 2 + 10}
        >
          <circle
            cx={radius + 5}
            cy={radius + 5}
            r={radius}
            stroke="#e5e7eb"
            strokeWidth="6"
            fill="none"
          />
          <circle
            cx={radius + 5}
            cy={radius + 5}
            r={radius}
            stroke={value >= 80 ? "#10b981" : value >= 60 ? "#f59e0b" : "#ef4444"}
            strokeWidth="6"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute flex flex-col items-center">
          <span className={`text-lg font-bold ${getCompletionColor()}`}>
            {Math.round(value)}%
          </span>
          <span className="text-xs text-gray-500">完整度</span>
        </div>
      </div>
    );
  };

  return (
    <Card className="w-full shadow-sm border border-gray-200 dark:border-gray-700">
      <CardHeader className="pb-2 px-3 pt-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <User className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <CardTitle className="text-base truncate">
                {studentInfo.name || "未填写"}
              </CardTitle>
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <School className="w-3 h-3" />
                <span className="truncate">{studentInfo.school || "未填写学校"}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-1.5">
            <CircularProgress value={completionRate} />
            <Button
              variant="ghost"
              size="sm"
              onClick={onEdit}
              className="h-7 w-7 p-0"
              aria-label="编辑信息"
            >
              <Edit className="w-3.5 h-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggle}
              className="h-7 w-7 p-0"
              aria-label={isExpandedState ? "收起信息" : "展开信息"}
            >
              {isExpandedState ? (
                <ChevronUp className="w-3.5 h-3.5" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5" />
              )}
            </Button>
          </div>
        </div>
        
        {/* 进度条 - 移动端更简洁 */}
        <div className="mt-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-600 dark:text-gray-400">信息完整度</span>
            <span className={`text-xs font-medium ${getCompletionColor()}`}>
              {Math.round(completionRate)}%
            </span>
          </div>
          <Progress value={completionRate} className="h-2" />
        </div>

        {completionRate < 80 && (
          <div className="mt-2 flex items-start gap-1.5 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
            <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-yellow-800 dark:text-yellow-300 font-medium">
                信息不足
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-0.5">
                补充信息可获得更精准推荐
              </p>
            </div>
          </div>
        )}
      </CardHeader>

      {isExpandedState && (
        <CardContent className="px-3 pt-0 pb-3 space-y-3">
          {/* 基本信息卡片 */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/10 dark:to-blue-800/10 rounded-lg p-3">
            <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2 flex items-center gap-1.5">
              <User className="w-4 h-4" />
              基本信息
            </h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">年级:</span>
                <span className="ml-1 font-medium">{studentInfo.grade || "未选择"}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">{isJunior ? "学段:" : "选科:"}</span>
                <span className="ml-1 font-medium">{getStreamText()}</span>
              </div>
              <div className="col-span-2">
                <span className="text-gray-500 dark:text-gray-400">目标层次:</span>
                <span className="ml-1">{getUniversityLevelBadge() || "未选择"}</span>
              </div>
            </div>
          </div>

          {/* 成绩卡片 */}
          {studentInfo.totalScore && (
            <div className="bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-900/10 dark:to-emerald-800/10 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                  <Award className="w-4 h-4" />
                  最近成绩
                </h4>
                <span className="text-lg font-bold text-green-700 dark:text-green-400">
                  {studentInfo.totalScore}分
                </span>
              </div>
              <div className="grid grid-cols-3 gap+1 text-sm">
                <div className="text-center">
                  <div className="text-gray-500 dark:text-gray-400 text-xs">语文</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">
                    {studentInfo.chineseScore || "-"}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-gray-500 dark:text-gray-400 text-xs">数学</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">
                    {studentInfo.mathScore || "-"}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-gray-500 dark:text-gray-400 text-xs">英语</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">
                    {studentInfo.englishScore || "-"}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 偏好卡片 */}
          {(Array.isArray(studentInfo.targetCities) && studentInfo.targetCities.length > 0) || (Array.isArray(studentInfo.preferredMajors) && studentInfo.preferredMajors.length > 0) && (
            <div className="space-y-2">
              {Array.isArray(studentInfo.targetCities) && studentInfo.targetCities.length > 0 && (
                <div className="bg-gradient-to-br from-purple-50 to-pink-100 dark:from-purple-900/10 dark:to-pink-800/10 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <MapPin className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">目标城市</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {studentInfo.targetCities.map((city, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs px-2 py-0.5">
                        {city}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {Array.isArray(studentInfo.preferredMajors) && studentInfo.preferredMajors.length > 0 && (
                <div className="bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-900/10 dark:to-orange-800/10 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <GraduationCap className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">偏好专业</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {studentInfo.preferredMajors.map((major, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs px-2 py-0.5">
                        {major}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 联系信息（如果有） */}
          {(extendedStudentInfo.phone || extendedStudentInfo.email || extendedStudentInfo.parentPhone) && (
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-lg p-3">
              <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                联系信息
              </h4>
              <div className="space-y-1.5 text-sm">
                {extendedStudentInfo.phone && (
                  <div className="flex items-center gap-1.5">
                    <Phone className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-700 dark:text-gray-300">{extendedStudentInfo.phone}</span>
                  </div>
                )}
                {extendedStudentInfo.email && (
                  <div className="flex items-center gap-1.5">
                    <Mail className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-700 dark:text-gray-300 truncate">{extendedStudentInfo.email}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}